import constants from 'constants/index';

/**
 * Reads `firm_details.subscription_details.access_modules` from the user API.
 * Only `true` enables a module; missing or non-boolean values are treated as disabled.
 *
 * @param {Record<string, unknown>|null|undefined} accessModules
 * @param {string} name - access_modules key
 * @returns {boolean}
 */
export function isAccessModuleEnabled(accessModules, name) {
  if (!accessModules || typeof accessModules !== 'object' || !name) {
    return false;
  }
  return accessModules[name] === true;
}

/**
 * Route + nav use `module: 'reports'`, but some plans (e.g. Solo) set `access_modules.reports: false`
 * while `my_time: true` so firms can use the My Time report without the full reports suite.
 */
export function canAccessReportsApplication(accessModules) {
  return isAccessModuleEnabled(accessModules, 'reports') || isAccessModuleEnabled(accessModules, 'my_time');
}

/**
 * Full reports suite is off but My Time is on (e.g. Solo): limit reports UI to My Time only.
 */
export function isMyTimeOnlyReportsPlan(accessModules) {
  return !isAccessModuleEnabled(accessModules, 'reports') && isAccessModuleEnabled(accessModules, 'my_time');
}

const BATCH_BILLING_PLANS = new Set(['alto', 'nimbus']);

/**
 * Batch Billing is included on Alto and Nimbus (per Stripe product metadata), but some tenants
 * have `access_modules.batch_billing` omitted. Respect an explicit false; otherwise infer from plan.
 *
 * @param {Record<string, unknown>|null|undefined} accessModules
 * @param {Record<string, unknown>|null|undefined} selectedPlanDetails - `subscription_details` from user API
 * @returns {boolean}
 */
export function isBatchBillingEnabled(accessModules, selectedPlanDetails) {
  const modules = accessModules && typeof accessModules === 'object' ? accessModules : {};
  if (modules.batch_billing === false) {
    return false;
  }
  if (isAccessModuleEnabled(modules, 'batch_billing')) {
    return true;
  }
  const fromPlanFields = String(selectedPlanDetails?.plan_name || selectedPlanDetails?.product_name || '')
    .trim()
    .toLowerCase();
  if (BATCH_BILLING_PLANS.has(fromPlanFields)) {
    return true;
  }
  return false;
}

/**
 * @param {Array<{ accessModule?: string }>} options
 * @param {(key: string) => boolean} checkSubPermission
 * @returns {Array}
 */
export function filterByAccessModule(options, checkSubPermission) {
  if (!Array.isArray(options)) {
    return [];
  }
  return options.filter((opt) => {
    const key = opt?.accessModule;
    if (!key) {
      return true;
    }
    return checkSubPermission(key);
  });
}

/**
 * Firm admin role from user API / Cognito. Some payloads use `firm-admin`, others `firm_admin`.
 *
 * @param {string|undefined|null} accessLevel
 * @returns {boolean}
 */
export function isFirmAdminAccessLevel(accessLevel) {
  const key = String(accessLevel || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
  return key === 'firm-admin';
}

/**
 * Whether a non-firm-admin user's firm subscription is expired/cancelled (user API). Used to
 * restrict in-app access and show the contact–Firm Admin banner; firm admins are never flagged.
 *
 * @param {string|undefined|null} accessLevel
 * @param {Record<string, unknown>|null|undefined} subscriptionDetails
 * @returns {boolean}
 */
export function shouldBlockNonFirmAdminForFirmSubscription(accessLevel, subscriptionDetails) {
  if (!accessLevel || isFirmAdminAccessLevel(accessLevel) || !subscriptionDetails) {
    return false;
  }
  if (subscriptionDetails.message === constants.NON_FIRM_ADMIN_SUBSCRIPTION_CANCEL_MESSAGE) {
    return true;
  }
  const cancelled = subscriptionDetails.is_cancelled === true || String(subscriptionDetails.status || '').toLowerCase() === 'cancelled';
  const noActivePeriodEnd = !subscriptionDetails.current_period_end;
  return cancelled && noActivePeriodEnd;
}

/**
 * User-facing copy for toast when {@link shouldBlockNonFirmAdminForFirmSubscription} applies.
 *
 * @param {Record<string, unknown>|null|undefined} subscriptionDetails
 * @returns {string}
 */
export function getNonFirmAdminFirmSubscriptionBlockMessage(subscriptionDetails) {
  if (subscriptionDetails?.message === constants.NON_FIRM_ADMIN_SUBSCRIPTION_CANCEL_MESSAGE) {
    return constants.NON_FIRM_ADMIN_SUBSCRIPTION_CANCEL_MESSAGE;
  }
  return constants.FIRM_USER_SUBSCRIPTION_EXPIRED_CONTACT_ADMIN;
}
